import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supportedLanguages } from '@/utils/supportedLanguages';
import {
  User,
  Moon,
  Sun,
  Globe,
  Calendar,
  Mail,
  KeyRound,
  Cookie,
} from 'lucide-react';
import { useAuth } from '@/context/Auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ChangePassword from '@/pages/ChangePassword';
import ChangeEmail from '@/pages/ChangeEmail';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/redux/store';
import { setDarkTheme } from '@/redux/userSlice';
import CookieSettingsDialog from '@/components/common/CookieSettingsDialog';
import { getCookieConsent } from '@/utils/cookieConsent';
import { useDateFormat } from '@/hooks/useDateFormat';

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const df = useDateFormat();
  const { user, updateUserSettings } = useAuth();
  const dispatch = useDispatch();
  const { darkTheme } = useSelector((state: RootState) => state.user.settings);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isEmailOpen, setIsEmailOpen] = React.useState(false);
  const [isCookieDialogOpen, setIsCookieDialogOpen] = React.useState(false);

  const changeLanguage = async (lng: string) => {
    i18n.changeLanguage(lng);
    await updateUserSettings({ preferred_language: lng });
  };

  const toggleDarkTheme = async () => {
    const newDarkTheme = !darkTheme;
    dispatch(setDarkTheme(newDarkTheme));
    await updateUserSettings({
      dark_theme: newDarkTheme,
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return df.dateLong(dateString);
  };

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-6 sm:space-y-8">
        {/* Profile Header */}
        <section className="flex w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
            <Avatar className="w-16 h-16 sm:w-20 sm:h-20">
              <AvatarImage src={user?.user_metadata.avatar_url} />
              <AvatarFallback>
                <User className="h-8 w-8 sm:h-10 sm:w-10" />
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col text-center sm:text-left">
              <Label className="text-xl sm:text-2xl font-semibold">
                {user?.user_metadata.full_name ||
                  t('profile.user_fallback.translation')}
              </Label>
              <Label className="text-sm text-muted-foreground">
                {user?.email}
              </Label>
            </div>
          </div>
        </section>

        {/* Account Information Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              description={t(
                'profile.account_information.description.translation'
              )}
            >
              <User className="h-5 w-5" />
              {t('profile.account_information.title.translation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1 rounded-lg bg-card p-3">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {t('profile.account_information.email_address.translation')}
                </Label>
                <p className="text-sm font-medium">{user?.email}</p>
              </div>
              <div className="flex flex-col space-y-1 rounded-lg bg-card p-3">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('profile.account_information.account_created.translation')}
                </Label>
                <p className="text-sm font-medium">
                  {formatDate(user?.created_at)}
                </p>
              </div>
              {user?.last_sign_in_at && (
                <div className="flex flex-col space-y-1 rounded-lg bg-card p-3">
                  <Label className="text-xs text-muted-foreground">
                    {t('profile.account_information.last_sign_in.translation')}
                  </Label>
                  <p className="text-sm font-medium">
                    {formatDate(user.last_sign_in_at)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Display Preferences Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              description={t(
                'profile.display_preferences.description.translation'
              )}
            >
              <Sun className="h-5 w-5" />
              {t('profile.display_preferences.title.translation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dark Theme Toggle */}
            <div className="flex items-center justify-between rounded-lg bg-card p-3">
              <div className="flex items-center gap-3">
                {darkTheme ? (
                  <Moon className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Sun className="h-5 w-5 text-muted-foreground" />
                )}
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">
                    {t(
                      'profile.display_preferences.dark_theme.title.translation'
                    )}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'profile.display_preferences.dark_theme.description.translation'
                    )}
                  </p>
                </div>
              </div>
              <Switch checked={darkTheme} onCheckedChange={toggleDarkTheme} />
            </div>

            {/* Language Selection */}
            <div className="flex flex-col gap-3 rounded-lg bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">
                    {t('profile.language.translation')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'profile.display_preferences.language_description.translation'
                    )}
                  </p>
                </div>
              </div>
              <Select onValueChange={changeLanguage} value={i18n.language}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue
                    placeholder={t('profile.select_language.translation')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map(lang => (
                    <SelectItem key={lang.code} value={lang.code || ''}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Cookies Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              description={t('profile.privacy_cookies.description.translation')}
            >
              <Cookie className="h-5 w-5" />
              {t('profile.privacy_cookies.title.translation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 rounded-lg bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Cookie className="h-5 w-5 text-muted-foreground" />
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">
                    {t(
                      'profile.privacy_cookies.cookie_settings.title.translation'
                    )}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {getCookieConsent()
                      ? t(
                          'profile.privacy_cookies.cookie_settings.description_configured.translation'
                        )
                      : t(
                          'profile.privacy_cookies.cookie_settings.description_not_configured.translation'
                        )}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsCookieDialogOpen(true)}
                className="w-full sm:w-auto"
              >
                {t('profile.privacy_cookies.manage_cookies.translation')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              description={t('profile.security.description.translation')}
            >
              <KeyRound className="h-5 w-5" />
              {t('profile.security.title.translation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Collapsible onOpenChange={setIsOpen} className="w-full">
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  data-active={isOpen ? 'true' : 'false'}
                  className="w-full sm:w-auto data-[active=true]:bg-muted transition-colors duration-300"
                >
                  {t('profile.change_password.translation')}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent open={isOpen}>
                <div className="mt-4 w-full">
                  <ChangePassword />
                </div>
              </CollapsibleContent>
            </Collapsible>
            <Collapsible onOpenChange={setIsEmailOpen} className="w-full">
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  data-active={isEmailOpen ? 'true' : 'false'}
                  className="w-full sm:w-auto data-[active=true]:bg-muted transition-colors duration-300"
                >
                  {t('profile.change_email.translation')}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent open={isEmailOpen}>
                <div className="mt-4 w-full">
                  <ChangeEmail />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/30 bg-destructive/5 dark:border-red-900/50">
          <CardHeader>
            <CardTitle
              className="text-destructive"
              description={t('profile.danger_zone.description.translation')}
            >
              {t('profile.danger_zone.title.translation')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button className="w-full sm:w-auto" variant="destructive" disabled>
              {t('profile.delete_account.translation')}
            </Button>
          </CardContent>
        </Card>

        {/* Cookie Settings Dialog */}
        <CookieSettingsDialog
          open={isCookieDialogOpen}
          onOpenChange={setIsCookieDialogOpen}
        />
      </div>
    </div>
  );
};

export default Profile;
